import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, ArrowRight, Briefcase, Plane, RefreshCw, Compass, Send, CheckCircle, Heart, FileText, CreditCard, Clock, Volume2, VolumeX, Users, Phone, Building2, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import '../styles/ChatAI.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const freelanceStepConfig = [
  { key: 'pending', label: 'Pending', icon: <Clock size={14} />, color: '#FFA500' },
  { key: 'visa_approved', label: 'Visa Approved', icon: <CheckCircle size={14} />, color: '#00FF7F' },
  { key: 'option_change', label: 'Option Change', icon: <RefreshCw size={14} />, color: '#FFD700' },
  { key: 'medical_test', label: 'Medical Test', icon: <Heart size={14} />, color: '#FF69B4' },
  { key: 'medical_done', label: 'Medical Done', icon: <CheckCircle size={14} />, color: '#FF69B4' },
  { key: 'residence_visa', label: 'Residence Visa', icon: <FileText size={14} />, color: '#00BFFF' },
  { key: 'emirates_id', label: 'Emirates ID', icon: <CreditCard size={14} />, color: '#9B59B6' },
];

const ChatAI = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatState, setChatState] = useState('menu');
  const [lookupData, setLookupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [awaitingAdminMsg, setAwaitingAdminMsg] = useState(false);
  const [adminMessageId, setAdminMessageId] = useState(null);
  const replyPollRef = useRef(null);
  const audioRef = useRef(null);
  const bodyRef = useRef(null);
  const lastSpokenIdx = useRef(-1);

  const serviceOptions = [
    { key: 'tourist_visa', label: t('chatTouristVisa') || 'Tourist Visa', icon: <Plane size={20} />, color: '#00BFFF', desc: t('chatTouristVisaDesc') || 'Apply for a Dubai tourist visa' },
    { key: 'visa_change', label: t('chatVisaChange') || 'Visa Change', icon: <RefreshCw size={20} />, color: '#FFD700', desc: t('chatVisaChangeDesc') || 'Change your current visa status' },
    { key: 'freelance_visa', label: t('chatFreelanceVisa') || 'Freelance Visa', icon: <Briefcase size={20} />, color: '#10B981', desc: t('chatFreelanceVisaDesc') || 'Get a freelance visa in Dubai' },
    { key: 'open_company', label: t('chatOpenCompany') || 'Open Company', icon: <Building2 size={20} />, color: '#9B59B6', desc: t('chatOpenCompanyDesc') || 'Start your business in UAE' },
    { key: 'tours', label: t('chatTours') || 'Tours', icon: <Compass size={20} />, color: '#FF69B4', desc: t('chatToursDesc') || 'Explore Dubai tours and activities' },
  ];

  useEffect(() => {
    if (isOpen) {
      setMessages([{ type: 'bot', text: t('chatAiGreeting') || 'Hello! How can I help you today?' }]);
      setChatState('main_menu');
      setLookupData(null);
      setInput('');
      lastSpokenIdx.current = -1;
    }
  }, [isOpen, t]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-speak new bot messages
  useEffect(() => {
    if (messages.length === 0) return;
    const lastIdx = messages.length - 1;
    const lastMsg = messages[lastIdx];
    if (lastIdx <= lastSpokenIdx.current) return;
    if (lastMsg.type !== 'bot') { lastSpokenIdx.current = lastIdx; return; }
    lastSpokenIdx.current = lastIdx;

    if (lastMsg.statusCard) {
      // Skip auto-speak for status card
      return;
    } else if (lastMsg.text) {
      speakText(lastMsg.text, `msg-${lastIdx}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeakingId(null);
  };

  const addMessage = (type, text, extra) => {
    setMessages(prev => [...prev, { type, text, ...extra }]);
  };

  // Poll for admin replies
  const startReplyPolling = (msgId) => {
    if (replyPollRef.current) clearInterval(replyPollRef.current);
    let lastReplyCount = 0;
    replyPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/chat/check-reply/${msgId}`);
        if (res.data.replies && res.data.replies.length > lastReplyCount) {
          const newReplies = res.data.replies.slice(lastReplyCount);
          newReplies.forEach(reply => {
            addMessage('bot', `${t('chatAdminReply') || 'Admin'}: ${reply.text}`);
          });
          lastReplyCount = res.data.replies.length;
        }
      } catch {
        // silently retry
      }
    }, 5000); // poll every 5 seconds
  };

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (replyPollRef.current) clearInterval(replyPollRef.current);
    };
  }, []);

  const handleMainMenuClick = (option) => {
    if (option === 'apply') {
      addMessage('user', t('chatApplyServices') || 'Apply for Services');
      setTimeout(() => {
        addMessage('bot', t('chatSelectService') || 'Please select a service:');
        setChatState('menu');
      }, 400);
    } else if (option === 'call_admin') {
      addMessage('user', t('chatCallAdmin') || 'Call Admin');
      setTimeout(() => {
        addMessage('bot', t('chatAskReferenceForAdmin') || 'Please enter your booking reference number first so admin can help you better:');
        setChatState('awaiting_admin_booking_ref');
      }, 400);
    } else {
      addMessage('user', t('chatCheckStatus') || 'Check Status');
      setTimeout(() => {
        addMessage('bot', t('chatAskReference') || 'Please enter your booking reference number:');
        setChatState('awaiting_reference');
      }, 400);
    }
  };

  const handleOptionClick = (option) => {
    addMessage('user', option.label);

    if (option.key === 'freelance_visa') {
      setTimeout(() => {
        addMessage('bot', t('chatAiRedirecting') || 'Taking you there now...');
        setTimeout(() => { onClose(); navigate('/freelance-visa'); }, 800);
      }, 500);
    } else if (option.key === 'open_company') {
      setTimeout(() => {
        addMessage('bot', t('chatAiRedirecting') || 'Taking you there now...');
        setTimeout(() => { onClose(); navigate('/start-business'); }, 800);
      }, 500);
    } else if (option.key === 'tours') {
      setTimeout(() => {
        addMessage('bot', t('chatAiRedirecting') || 'Taking you there now...');
        setTimeout(() => { onClose(); navigate('/tours'); }, 800);
      }, 500);
    } else {
      setTimeout(() => {
        addMessage('bot', t('chatAiRedirecting') || 'Taking you there now...');
        setTimeout(() => {
          onClose();
          const serviceSection = document.getElementById('services');
          if (serviceSection) serviceSection.scrollIntoView({ behavior: 'smooth' });
        }, 800);
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const value = input.trim();
    setInput('');

    if (chatState === 'awaiting_admin_booking_ref') {
      addMessage('user', value);
      setLoading(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/chatai/lookup`, {
          bookingNumber: value,
          step: 'reference'
        });
        if (!res.data.found) {
          addMessage('bot', t('chatBookingNotFound') || 'Booking not found. Please check your reference number and try again.');
        } else {
          setLookupData({ ...res.data, bookingNumber: value });
          const name = res.data.name || '';
          const greeting = t('chatAdminMessageReady')
            ? t('chatAdminMessageReady').replace('{name}', name)
            : `Thanks${name ? ' ' + name : ''}! Now write your message and we will forward it to the admin.`;
          addMessage('bot', greeting);
          setChatState('awaiting_admin_message');
        }
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (chatState === 'awaiting_admin_message') {
      addMessage('user', value);
      setLoading(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/chat/send-to-admin`, {
          message: value,
          name: lookupData?.name || 'Website Visitor',
          bookingNumber: lookupData?.bookingNumber || '',
          phone: lookupData?.mobile || '',
        });
        if (res.data.success) {
          const msgId = res.data.messageId;
          setAdminMessageId(msgId);
          addMessage('bot', t('chatMessageSentAdmin') || 'Your message has been sent to the admin. Please wait, they will reply soon...');
          setChatState('waiting_admin_reply');
          // Start polling for admin reply
          if (msgId) {
            startReplyPolling(msgId);
          }
        }
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (chatState === 'waiting_admin_reply') {
      addMessage('user', value);
      setLoading(true);
      try {
        await axios.post(`${BACKEND_URL}/api/chat/send-to-admin`, {
          message: value,
          name: lookupData?.name || 'Website Visitor',
          bookingNumber: lookupData?.bookingNumber || '',
          phone: lookupData?.mobile || '',
        });
        addMessage('bot', t('chatMessageSentAdmin') || 'Your message has been sent to the admin. Please wait for their reply...');
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (chatState === 'awaiting_reference') {
      addMessage('user', value);
      setLoading(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/chatai/lookup`, {
          bookingNumber: value,
          step: 'reference'
        });
        if (!res.data.found) {
          addMessage('bot', t('chatBookingNotFound') || 'Booking not found. Please check your reference number and try again.');
        } else if (res.data.step === 'ask_passport') {
          setLookupData({ ...res.data, bookingNumber: value });
          addMessage('bot', t('chatAskPassport') || 'Booking found! Please enter your passport number to verify:');
          setChatState('awaiting_passport');
        } else {
          setLookupData({ ...res.data, bookingNumber: value });
          showStatusResult({ ...res.data, bookingNumber: value });
        }
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (chatState === 'awaiting_passport') {
      addMessage('user', value);
      setLoading(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/chatai/lookup`, {
          bookingNumber: lookupData.bookingNumber,
          passportNumber: value,
          step: 'passport'
        });
        if (!res.data.verified) {
          addMessage('bot', t('chatPassportMismatch') || 'Passport number does not match. Please try again.');
        } else {
          setLookupData({ ...res.data, bookingNumber: lookupData.bookingNumber });
          showStatusResult(res.data);
        }
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (chatState === 'showing_status') {
      addMessage('user', value);

      // If awaiting the admin message, send it directly
      if (awaitingAdminMsg) {
        setLoading(true);
        try {
          await axios.post(`${BACKEND_URL}/api/chat/send-to-admin`, {
            message: value,
            name: lookupData?.name || 'Chat AI User',
            bookingNumber: lookupData?.bookingNumber || '',
            phone: lookupData?.mobile || '',
          });
          addMessage('bot', t('chatSentToAdmin') || 'Your message has been sent to the admin. They will contact you soon.');
          setAwaitingAdminMsg(false);
          setQuestionCount(0);
        } catch {
          addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      const newCount = questionCount + 1;
      setQuestionCount(newCount);
      try {
        const docs = lookupData?.stepDocuments || [];
        const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
        const currentStep = lookupData?.freelanceStep || lookupData?.status || 'pending';
        const stepIdx = freelanceStepConfig.findIndex(s => s.key === currentStep);
        const completedSteps = freelanceStepConfig.filter((_, i) => i <= stepIdx).map(s => s.label).join(', ');

        // Calculate 58-day countdown from option_change date
        const history = lookupData?.statusHistory || [];
        let daysFromChange = null;
        let daysRemaining = null;
        const changeEntry = [...history].reverse().find(h => h.to === 'option_change');
        if (changeEntry?.changedAt) {
          const changeDate = new Date(changeEntry.changedAt);
          const now = new Date();
          daysFromChange = Math.floor((now - changeDate) / (1000 * 60 * 60 * 24));
          daysRemaining = Math.max(0, 58 - daysFromChange);
        }

        const context = {
          name: lookupData?.name || '',
          service: lookupData?.service || 'Freelance Visa',
          currentStep: freelanceStepConfig.find(s => s.key === currentStep)?.label || currentStep,
          agentName: lookupData?.agentName || '',
          agentPhone: lookupData?.agentPhone || '',
          lastDocument: lastDoc ? `${lastDoc.filename || 'Document'} (step: ${lastDoc.step})` : 'None yet',
          completedSteps: completedSteps || 'None',
          daysFromChange: daysFromChange !== null ? `${daysFromChange} days since status change` : 'Not available',
          daysRemaining: daysRemaining !== null ? `${daysRemaining} days remaining out of 58` : 'Not available',
        };
        const res = await axios.post(`${BACKEND_URL}/api/chat/ask`, { question: value, context });
        addMessage('bot', res.data.answer);

        // After 2 questions, offer to send message to admin
        if (newCount >= 2) {
          setTimeout(() => {
            addMessage('bot', t('chatOfferAdmin') || 'Would you like me to send your message to the admin? Please write clearly what you need and I will forward it.');
            setAwaitingAdminMsg(true);
          }, 800);
        }
      } catch {
        addMessage('bot', t('chatError') || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Get contextual explanation based on current visa step
  const getStepExplanation = (step, data) => {
    const lang = localStorage.getItem('registan-lang') || 'en';

    // Calculate 58-day countdown from option_change date
    const history = data?.statusHistory || [];
    let countdownText = '';
    const changeEntry = [...history].reverse().find(h => h.to === 'option_change');
    if (changeEntry?.changedAt) {
      const changeDate = new Date(changeEntry.changedAt);
      const now = new Date();
      const daysPassed = Math.floor((now - changeDate) / (1000 * 60 * 60 * 24));
      const daysLeft = Math.max(0, 58 - daysPassed);
      if (lang === 'ru') {
        countdownText = ` Прошло ${daysPassed} дней с момента смены статуса. Осталось ${daysLeft} дней из 58.`;
      } else if (lang === 'uz') {
        countdownText = ` Status o'zgarganidan ${daysPassed} kun o'tdi. 58 kundan ${daysLeft} kun qoldi.`;
      } else {
        countdownText = ` It has been ${daysPassed} days since your status change. ${daysLeft} days remaining out of 58.`;
      }
    }

    const explanations = {
      pending: {
        en: 'Your current visa is pending for approval. Please wait, we will notify you once it is approved.',
        ru: 'Ваша виза ожидает одобрения. Пожалуйста, подождите, мы уведомим вас, как только она будет одобрена.',
        uz: 'Vizangiz tasdiqlash kutilmoqda. Iltimos, kuting, tasdiqlangandan so\'ng sizga xabar beramiz.',
      },
      visa_approved: {
        en: 'Great news! Your visa has been approved. We are now processing your status change. Once it is done, our system will notify you.',
        ru: 'Отличные новости! Ваша виза одобрена. Мы сейчас оформляем смену статуса. Как только всё будет готово, наша система вас уведомит.',
        uz: 'Ajoyib yangilik! Vizangiz tasdiqlandi. Hozir status o\'zgartirishni amalga oshiryapmiz. Tayyor bo\'lganda tizimimiz sizga xabar beradi.',
      },
      option_change: {
        en: 'Your option/status change is in progress. This step involves updating your visa category.' + countdownText,
        ru: 'Смена опции/статуса в процессе. На этом этапе обновляется категория вашей визы.' + countdownText,
        uz: 'Opsiya/status o\'zgartirish jarayonda. Bu bosqichda viza toifangiz yangilanadi.' + countdownText,
      },
      medical_test: {
        en: 'You need to complete your medical fitness test. Please visit the approved medical center. Once done, upload your results or inform your agent.' + countdownText,
        ru: 'Вам необходимо пройти медицинское обследование. Посетите одобренный медицинский центр. После завершения загрузите результаты или сообщите агенту.' + countdownText,
        uz: 'Tibbiy ko\'rikdan o\'tishingiz kerak. Tasdiqlangan tibbiy markazga tashrif buyuring. Tayyor bo\'lgach natijalarni yuklang yoki agentingizga xabar bering.' + countdownText,
      },
      medical_done: {
        en: 'Your medical test is completed. We are now processing the next step — your residence visa. Please wait for the update.' + countdownText,
        ru: 'Медицинское обследование завершено. Мы обрабатываем следующий этап — резидентскую визу. Пожалуйста, ожидайте обновления.' + countdownText,
        uz: 'Tibbiy ko\'rik yakunlandi. Keyingi bosqich — rezident viza tayyorlanmoqda. Yangilanishni kuting.' + countdownText,
      },
      residence_visa: {
        en: 'Your residence visa has been stamped! You are now free to travel and leave Dubai. Next step is Emirates ID.' + countdownText,
        ru: 'Ваша резидентская виза проштампована! Теперь вы можете свободно путешествовать и покидать Дубай. Следующий этап — Emirates ID.' + countdownText,
        uz: 'Rezident vizangiz shtamplandi! Endi Dubaydan erkin chiqishingiz mumkin. Keyingi bosqich — Emirates ID.' + countdownText,
      },
      emirates_id: {
        en: 'Your Emirates ID is being processed. This is the final step! You can leave Dubai anytime, but please wait for your Emirates ID card delivery. Make sure your delivery address is correct.' + countdownText,
        ru: 'Ваш Emirates ID оформляется. Это последний этап! Вы можете покинуть Дубай в любое время, но дождитесь доставки карты Emirates ID. Убедитесь, что адрес доставки правильный.' + countdownText,
        uz: 'Emirates ID tayyorlanmoqda. Bu oxirgi bosqich! Dubaydan istalgan vaqtda chiqishingiz mumkin, lekin Emirates ID kartasi yetkazilishini kuting. Yetkazish manzilingiz to\'g\'ri ekanligiga ishonch hosil qiling.' + countdownText,
      },
    };
    return explanations[step]?.[lang] || explanations[step]?.en || '';
  };

  const showStatusResult = (data) => {
    const welcomeText = `${t('chatWelcome') || 'Welcome'}, ${data.name}!`;
    addMessage('bot', welcomeText);

    setTimeout(() => {
      const currentStep = data.freelanceStep || data.status || 'pending';
      const cardData = { ...data, currentStep, stepDocuments: data.stepDocuments || [] };
      addMessage('bot', null, { statusCard: true, data: cardData });
      setChatState('showing_status');

      // Add contextual explanation based on current step
      const explanation = getStepExplanation(currentStep, data);
      if (explanation) {
        setTimeout(() => addMessage('bot', explanation), 500);
      }

      // Auto-speak the current status after showing card
      const stepInfo = freelanceStepConfig.find(s => s.key === currentStep);
      const statusText = `${data.name}, ${t('chatCurrentStatus') || 'your current status is'} ${stepInfo?.label || currentStep}`;
      setTimeout(() => speakText(statusText, 'status-auto'), 400);
    }, 600);
  };

  const handleBack = () => {
    stopAudio();
    if (replyPollRef.current) clearInterval(replyPollRef.current);
    setMessages([{ type: 'bot', text: t('chatAiGreeting') || 'Hello! How can I help you today?' }]);
    setChatState('main_menu');
    setLookupData(null);
    setInput('');
    setQuestionCount(0);
    setAwaitingAdminMsg(false);
    setAdminMessageId(null);
  };

  // Speak any text via TTS
  const speakText = async (text, msgId) => {
    // If same message is playing, stop it
    if (speakingId === msgId && audioRef.current) {
      stopAudio();
      return;
    }
    // Stop any currently playing audio
    stopAudio();

    if (!text) return;

    const lang = localStorage.getItem('registan-lang') || 'en';
    const audio = new Audio();
    audioRef.current = audio;
    setSpeakingId(msgId);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/tts`, { text, lang });
      if (res.data.audio) {
        audio.src = `data:audio/mpeg;base64,${res.data.audio}`;
        audio.onended = () => { setSpeakingId(null); audioRef.current = null; };
        audio.onerror = () => { setSpeakingId(null); audioRef.current = null; };
        audio.play().catch(() => { setSpeakingId(null); audioRef.current = null; });
      } else {
        setSpeakingId(null);
        audioRef.current = null;
      }
    } catch {
      setSpeakingId(null);
      audioRef.current = null;
    }
  };

  // Build voice text for status card
  const getStatusVoiceText = (data) => {
    const currentStep = data.currentStep || 'pending';
    const stepInfo = freelanceStepConfig.find(s => s.key === currentStep);
    const currentIdx = freelanceStepConfig.findIndex(x => x.key === currentStep);
    const completedSteps = freelanceStepConfig.filter((_, i) => i <= currentIdx);
    const remainingSteps = freelanceStepConfig.filter((_, i) => i > currentIdx);
    const lang = localStorage.getItem('registan-lang') || 'en';

    if (lang === 'ru') {
      return `Добро пожаловать, ${data.name}. Статус фрилансерской визы. Номер: ${data.bookingNumber}. Текущий этап: ${stepInfo?.label || currentStep}. Завершённые: ${completedSteps.map(s => s.label).join(', ')}. ${remainingSteps.length > 0 ? `Оставшиеся: ${remainingSteps.map(s => s.label).join(', ')}.` : 'Все этапы завершены!'} ${data.agentName ? `Агент: ${data.agentName}.` : ''}`;
    } else if (lang === 'uz') {
      return `Xush kelibsiz, ${data.name}. Frilanser viza holati. Raqam: ${data.bookingNumber}. Joriy: ${stepInfo?.label || currentStep}. Yakunlangan: ${completedSteps.map(s => s.label).join(', ')}. ${remainingSteps.length > 0 ? `Qolgan: ${remainingSteps.map(s => s.label).join(', ')}.` : "Hammasi yakunlandi!"} ${data.agentName ? `Agent: ${data.agentName}.` : ''}`;
    }
    return `Welcome, ${data.name}. Freelance visa status. Reference: ${data.bookingNumber}. Current step: ${stepInfo?.label || currentStep}. Completed: ${completedSteps.map(s => s.label).join(', ')}. ${remainingSteps.length > 0 ? `Remaining: ${remainingSteps.map(s => s.label).join(', ')}.` : 'All steps completed!'} ${data.agentName ? `Agent: ${data.agentName}.` : ''}`;
  };

  if (!isOpen) return null;

  return (
    <div className="chatai-overlay" onClick={onClose}>
      <div className="chatai-panel" onClick={(e) => e.stopPropagation()} data-testid="chat-ai-panel">
        <div className="chatai-header">
          <div className="chatai-header-left">
            <div className="chatai-avatar">
              <MessageSquare size={20} />
            </div>
            <div>
              <h3>Registan AI</h3>
              <span className="chatai-status">{t('chatOnline') || 'Online'}</span>
            </div>
          </div>
          <button className="chatai-close" onClick={onClose} data-testid="chatai-close">
            <X size={20} />
          </button>
        </div>

        <div className="chatai-body" ref={bodyRef}>
          {messages.map((msg, i) => (
            <div key={i} className={`chatai-message chatai-${msg.type}`}>
              {msg.statusCard ? (
                <div className="chatai-status-card" data-testid="chatai-status-card">
                  <div className="chatai-status-header">
                    <Briefcase size={16} />
                    <span>{msg.data.service || 'Freelance Visa'}</span>
                    <span className="chatai-ref">#{msg.data.bookingNumber}</span>
                  </div>
                  <div className="chatai-steps-timeline">
                    {freelanceStepConfig.map((step, idx) => {
                      const currentIdx = freelanceStepConfig.findIndex(s => s.key === msg.data.currentStep);
                      const isDone = idx <= currentIdx;
                      const isCurrent = idx === currentIdx;
                      return (
                        <div key={step.key} className={`chatai-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
                          <div className="chatai-step-dot" style={{ background: isDone ? step.color : 'var(--border-color)', borderColor: isCurrent ? step.color : 'transparent' }}>
                            {isDone ? step.icon : <span>{idx + 1}</span>}
                          </div>
                          <span className="chatai-step-label" style={{ color: isDone ? step.color : 'var(--text-muted)' }}>
                            {step.label}
                          </span>
                          {idx < freelanceStepConfig.length - 1 && (
                            <div className="chatai-step-line" style={{ background: isDone && idx < currentIdx ? step.color : 'var(--border-color)' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="chatai-status-current">
                    {t('chatCurrentStatus') || 'Current Status'}: <strong style={{ color: freelanceStepConfig.find(s => s.key === msg.data.currentStep)?.color || '#FFA500' }}>
                      {freelanceStepConfig.find(s => s.key === msg.data.currentStep)?.label || msg.data.currentStep}
                    </strong>
                  </div>

                  {/* Status Card Voice Button */}
                  <button
                    className={`chatai-voice-btn ${speakingId === `status-${i}` ? 'playing' : ''}`}
                    onClick={() => speakText(getStatusVoiceText(msg.data), `status-${i}`)}
                    data-testid="chatai-voice-btn"
                  >
                    {speakingId === `status-${i}` ? (
                      <>
                        <VolumeX size={16} />
                        <span>{t('chatVoiceStop') || 'Stop'}</span>
                        <div className="chatai-audio-wave">
                          <span /><span /><span /><span />
                        </div>
                      </>
                    ) : (
                      <>
                        <Volume2 size={16} />
                        <span>{t('chatVoiceListen') || 'Listen to Status'}</span>
                      </>
                    )}
                  </button>

                  {/* Who is my agent button */}
                  {msg.data.agentName && (
                    <button
                      className={`chatai-agent-btn ${speakingId === `agent-${i}` ? 'playing' : ''}`}
                      onClick={() => {
                        const agentText = `${t('chatYourAgent') || 'Your agent is'} ${msg.data.agentName}`;
                        speakText(agentText, `agent-${i}`);
                      }}
                      data-testid="chatai-agent-btn"
                    >
                      {speakingId === `agent-${i}` ? (
                        <>
                          <VolumeX size={16} />
                          <span>{msg.data.agentName}</span>
                          <div className="chatai-audio-wave">
                            <span /><span /><span /><span />
                          </div>
                        </>
                      ) : (
                        <>
                          <Users size={16} />
                          <span>{t('chatWhoIsMyAgent') || 'Who is my agent?'}</span>
                        </>
                      )}
                    </button>
                  )}

                  {/* Ask agent number button */}
                  {msg.data.agentPhone && (
                    <button
                      className="chatai-phone-btn"
                      onClick={() => {
                        addMessage('bot', `${t('chatAgentNumber') || 'Agent number'}: ${msg.data.agentPhone}`);
                      }}
                      data-testid="chatai-phone-btn"
                    >
                      <Phone size={16} />
                      <span>{t('chatAskAgentNumber') || 'Ask Agent Number'}</span>
                    </button>
                  )}

                  {msg.data.stepDocuments && msg.data.stepDocuments.length > 0 && (
                    <div className="chatai-docs-section">
                      <div className="chatai-docs-title">
                        <FileText size={14} /> {t('chatApprovedDocs') || 'Approved Documents'}
                      </div>
                      {msg.data.stepDocuments.map((doc, dIdx) => {
                        const stepInfo = freelanceStepConfig.find(s => s.key === doc.step);
                        const docUrl = doc.url?.startsWith('http') ? doc.url : `${BACKEND_URL}/uploads/${doc.url}`;
                        return (
                          <a key={dIdx} href={docUrl} target="_blank" rel="noopener noreferrer" className="chatai-doc-item" download>
                            <div className="chatai-doc-icon" style={{ background: `${stepInfo?.color || '#5EFCE8'}20`, color: stepInfo?.color || '#5EFCE8' }}>
                              {stepInfo?.icon || <FileText size={14} />}
                            </div>
                            <div className="chatai-doc-info">
                              <span className="chatai-doc-name">{doc.filename || 'Document'}</span>
                              <span className="chatai-doc-step">{stepInfo?.label || doc.step}</span>
                            </div>
                            <ArrowRight size={14} className="chatai-doc-download" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="chatai-bubble-wrap">
                  <div className={`chatai-bubble ${msg.type === 'bot' && msg.text?.includes('Taking') ? 'chatai-redirect' : ''}`}>
                    {msg.text}
                  </div>
                  {msg.type === 'bot' && msg.text && (
                    <button
                      className={`chatai-msg-speaker ${speakingId === `msg-${i}` ? 'active' : ''}`}
                      onClick={() => speakText(msg.text, `msg-${i}`)}
                      data-testid={`chatai-speaker-${i}`}
                      title={speakingId === `msg-${i}` ? 'Stop' : 'Listen'}
                    >
                      {speakingId === `msg-${i}` ? (
                        <>
                          <VolumeX size={13} />
                          <div className="chatai-mini-wave"><span /><span /><span /></div>
                        </>
                      ) : (
                        <Volume2 size={13} />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="chatai-message chatai-bot">
              <div className="chatai-bubble chatai-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}

          {chatState === 'main_menu' && (
            <div className="chatai-options">
              <button
                className="chatai-option"
                onClick={() => handleMainMenuClick('apply')}
                data-testid="chatai-option-apply"
                style={{ '--option-color': '#10B981' }}
              >
                <div className="chatai-option-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                  <Briefcase size={20} />
                </div>
                <div className="chatai-option-text">
                  <span className="chatai-option-label">{t('chatApplyServices') || 'Apply for Services'}</span>
                  <span className="chatai-option-desc">{t('chatApplyServicesDesc') || 'Browse and apply for visa services'}</span>
                </div>
                <ArrowRight size={16} className="chatai-option-arrow" />
              </button>
              <button
                className="chatai-option"
                onClick={() => handleMainMenuClick('status')}
                data-testid="chatai-option-status"
                style={{ '--option-color': '#3B82F6' }}
              >
                <div className="chatai-option-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}>
                  <CheckCircle size={20} />
                </div>
                <div className="chatai-option-text">
                  <span className="chatai-option-label">{t('chatCheckStatus') || 'Check Status'}</span>
                  <span className="chatai-option-desc">{t('chatCheckStatusDesc') || 'Track your visa application'}</span>
                </div>
                <ArrowRight size={16} className="chatai-option-arrow" />
              </button>
              <button
                className="chatai-option chatai-call-btn"
                onClick={() => handleMainMenuClick('call_admin')}
                data-testid="chatai-option-call"
                style={{ '--option-color': '#EF4444' }}
              >
                <div className="chatai-option-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                  <MessageCircle size={20} />
                </div>
                <div className="chatai-option-text">
                  <span className="chatai-option-label">{t('chatCallAdmin') || 'Call Admin'}</span>
                  <span className="chatai-option-desc">{t('chatCallAdminDesc') || 'Send a message to admin'}</span>
                </div>
                <ArrowRight size={16} className="chatai-option-arrow" />
              </button>
            </div>
          )}

          {chatState === 'menu' && (
            <div className="chatai-options">
              {serviceOptions.map((option) => (
                <button
                  key={option.key}
                  className="chatai-option"
                  onClick={() => handleOptionClick(option)}
                  data-testid={`chatai-option-${option.key}`}
                  style={{ '--option-color': option.color }}
                >
                  <div className="chatai-option-icon" style={{ background: `${option.color}20`, color: option.color }}>
                    {option.icon}
                  </div>
                  <div className="chatai-option-text">
                    <span className="chatai-option-label">{option.label}</span>
                    <span className="chatai-option-desc">{option.desc}</span>
                  </div>
                  <ArrowRight size={16} className="chatai-option-arrow" />
                </button>
              ))}
              <div className="chatai-back-row">
                <button className="chatai-back-btn" onClick={handleBack} data-testid="chatai-back-main">
                  {t('chatBackToMenu') || 'Back to Menu'}
                </button>
              </div>
            </div>
          )}

          {chatState === 'showing_status' && (
            <div className="chatai-back-row">
              <button className="chatai-back-btn" onClick={handleBack} data-testid="chatai-back">
                {t('chatBackToMenu') || 'Back to Menu'}
              </button>
            </div>
          )}

          {(chatState === 'awaiting_admin_booking_ref' || chatState === 'awaiting_admin_message' || chatState === 'waiting_admin_reply') && (
            <div className="chatai-back-row">
              <button className="chatai-back-btn" onClick={handleBack} data-testid="chatai-back-admin">
                {t('chatBackToMenu') || 'Back to Menu'}
              </button>
            </div>
          )}
        </div>

        {(chatState === 'awaiting_reference' || chatState === 'awaiting_passport' || chatState === 'showing_status' || chatState === 'awaiting_admin_booking_ref' || chatState === 'awaiting_admin_message' || chatState === 'waiting_admin_reply') && (
          <form className="chatai-input-bar" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                chatState === 'awaiting_reference' ? (t('chatRefPlaceholder') || 'e.g. RAMIZ20260001')
                : chatState === 'awaiting_admin_booking_ref' ? (t('chatRefPlaceholder') || 'e.g. RAMIZ20260001')
                : chatState === 'awaiting_passport' ? (t('chatPassportPlaceholder') || 'e.g. FA3204764')
                : chatState === 'awaiting_admin_message' ? (t('chatWriteMessagePlaceholder') || 'Write your message to admin...')
                : chatState === 'waiting_admin_reply' ? (t('chatWriteMorePlaceholder') || 'Send another message...')
                : (t('chatAskQuestion') || 'Ask about your visa...')
              }
              autoFocus
              data-testid="chatai-input"
            />
            <button type="submit" disabled={!input.trim() || loading} data-testid="chatai-send">
              <Send size={18} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatAI;
